import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Typography,
  Box,
  CircularProgress,
  Alert
} from '@mui/material';
import { api } from '../services/api';

const InfoAdicionalForm = ({ open, onClose, ticketCodigo, onSuccess }) => {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [preguntas, setPreguntas] = useState([]);
  const [respuestas, setRespuestas] = useState({});
  const [error, setError] = useState('');

  const fetchInformacion = async () => {
    if (!ticketCodigo) return;

    setLoading(true);
    setError('');

    try {
      const response = await api.tickets.getInformacionAdicional(ticketCodigo);

      if (response.status === 'success') {
        setPreguntas(response.data.preguntas || []);

        if (response.data.ya_cargado) {
          // una vez cargada, no se vuelve a mostrar por este medio (ni para quien la cargo)
          setError('Este ticket ya tiene información cargada. Una vez cargada no se puede ver ni modificar.');
          return;
        }

        setRespuestas({});

      } else {
        setError(response.message || 'Error al cargar información');
      }
    } catch (error) {
      setError('Error de conexión');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) {
      fetchInformacion();
    }
  }, [open, ticketCodigo]);

  const handleRespuestaChange = (preguntaId, valor) => {
    setRespuestas(prev => ({
      ...prev,
      [preguntaId]: valor
    }));
  };

  const handleSubmit = async () => {
    setSaving(true);
    setError('');
    
    try {
      const response = await api.tickets.saveInformacionAdicional(
        ticketCodigo,
        respuestas
      );

      if (response.status === 'success') {
        onSuccess?.();
        onClose();
      } else {
        setError(response.message || 'Error al guardar información');
      }
    } catch (error) {
      setError('Error de conexión');
    } finally {
      setSaving(false);
    }
  };

  const isFormValid = () => {
    return preguntas.every(pregunta =>
      !pregunta.obligatoria || (respuestas[pregunta.id] && respuestas[pregunta.id].trim().length > 0)
    );
  };

  return (
    <Dialog 
      open={open} 
      onClose={onClose} 
      maxWidth="md" 
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: '12px',
          padding: '8px'
        }
      }}
    >
      <DialogTitle sx={{
        fontFamily: 'Bricolage Grotesque',
        fontWeight: 600,
        color: '#0B0060',
        fontSize: '20px'
      }}>
        Completar Información Adicional
      </DialogTitle>
      
      <DialogContent>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress sx={{ color: '#5656FF' }} />
          </Box>
        ) : error ? (
          <Alert severity="error" sx={{ mb: 2, fontFamily: 'Bricolage Grotesque' }}>
            {error}
          </Alert>
        ) : (
          <Box sx={{ mt: 1 }}>
            <Typography variant="body2" sx={{
              mb: 3,
              color: '#6B7280',
              fontFamily: 'Bricolage Grotesque'
            }}>
              Por favor, completa la siguiente información para tu ticket:
            </Typography>
            
            {preguntas.map((pregunta, index) => (
              <TextField
                key={pregunta.id}
                fullWidth
                label={pregunta.obligatoria ? `${pregunta.informacion_adicional} *` : pregunta.informacion_adicional}
                value={respuestas[pregunta.id] || ''}
                onChange={(e) => handleRespuestaChange(pregunta.id, e.target.value)}
                margin="normal"
                variant="outlined"
                sx={{
                  mb: 2,
                  '& .MuiOutlinedInput-root': {
                    fontFamily: 'Bricolage Grotesque',
                    '& fieldset': {
                      borderColor: '#E6E6FF',
                    },
                    '&:hover fieldset': {
                      borderColor: '#5656FF',
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: '#5656FF',
                    },
                  },
                  '& .MuiInputLabel-root': {
                    fontFamily: 'Bricolage Grotesque',
                    color: '#6B7280',
                    '&.Mui-focused': {
                      color: '#5656FF',
                    },
                  },
                }}
              />
            ))}
          </Box>
        )}
      </DialogContent>
      
      <DialogActions sx={{ px: 3, pb: 3 }}>
        <Button 
          onClick={onClose}
          sx={{
            fontFamily: 'Bricolage Grotesque',
            color: '#6B7280',
            textTransform: 'none'
          }}
        >
          Cancelar
        </Button>

        {!error && (
          <Button
            onClick={handleSubmit}
            disabled={saving || !isFormValid()}
            variant="contained"
            sx={{
              fontFamily: 'Bricolage Grotesque',
              backgroundColor: '#5656FF',
              textTransform: 'none',
              px: 3,
              '&:hover': {
                backgroundColor: '#0B0060'
              },
              '&:disabled': {
                backgroundColor: '#cccccc'
              }
            }}
          >
            {saving ? <CircularProgress size={20} sx={{ mr: 1 }} /> : null}
            {saving ? 'Guardando...' : 'Guardar Información'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default InfoAdicionalForm;